import { AutocompleteValue, AutocompleteFreeSoloValueMapping, Paper } from "@mui/material";
import MuiAutocomplete, {
	AutocompleteCloseReason,
	AutocompleteRenderInputParams,
	AutocompleteRenderOptionState,
	AutocompleteProps as MuiAutocompleteProps
} from "@mui/material/Autocomplete";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Popper, { PopperProps } from "@mui/material/Popper";
import { styled } from "@mui/material/styles";
import { useLocale } from "designSystem/locale";
import { boxShadows, grey, red, white } from "designSystem/styles/color";
import { ComponentItem } from "designSystem/types/component-item-types";
import { stopMuiEvent } from "designSystem/utilities/prevent-default-behavior";
import React, { FunctionComponent, HTMLAttributes, KeyboardEventHandler, ReactElement, ReactNode, useMemo, useRef } from "react";
import { ButtonProps } from "../button/Button";
import OverflowText from "../text/OverflowText";
import SearchTextField, { SearchTextFieldProps } from "../textfield/SearchTextField";
import { largeListThreshold } from "designSystem/settings";

const INVALID_BORDER_CLASS = "AutoCompleteListInvalidBorder";
const FOCUSED_OPTION_CSS = ".MuiAutocomplete-option.Mui-focused";
export const POPPER_MAX_WIDTH = "400px";

const StyledAutoComplete = styled(MuiAutocomplete)(({
	theme,
}) => ({
	["&&& .MuiAutocomplete-inputRoot"]: {
		paddingRight: theme.spacing(1),
	},
	["&.MuiAutocomplete-hasClearIcon.MuiAutocomplete-hasPopupIcon"]: {
		"&&& .MuiAutocomplete-inputRoot": {
			paddingRight: 56,
		},
	},
	[`&.${INVALID_BORDER_CLASS}`]: {
		"& .MuiInputBase-root": {
			borderColor: `${red[600]} !important`,
		},
	},
})) as typeof MuiAutocomplete;

const StyledPaper = styled(Paper)(({
	theme,
}) => ({
	backgroundColor: white[50],
	boxShadow: boxShadows.popper,
	"& > ul": {
		margin: 0,
		padding: "4px 0",
		"& > li": {
			padding: 0,
			"& button": {
				height: 26,
				justifyContent: "left",
				margin: "0 !important",
				padding: "0 8px",
				transition: "none",
				whiteSpace: "nowrap",				
				width: "100%",
				"&:hover": {
					backgroundColor: grey[200],
				},
			},
		},
	},
}));

interface SelectActionsListProps {
	onClose?: (event: React.SyntheticEvent<Element, Event>, reason: AutocompleteCloseReason) => void;
	selectActions: ReactElement<ButtonProps>[];
}

const SelectActionsList: FunctionComponent<SelectActionsListProps> = (props) => {
	const {
		onClose,
		selectActions,
	} = props;
	return (
		<StyledPaper>
			<List role="listbox">
				{selectActions.map((action: ReactElement<ButtonProps>, i: number): ReactNode => {
					// please note: the onPopperMouseDown and onPopperMouseUp methods declared below are part of implementing
					// the actions menu functionality of the SingleSelect and MultiSelect components.  As of July 2022,
					// DOM elements belonging to the actions menu are not receiving mouse 'click' events. The parent Popper component
					// stops them from bubbling up.
					//
					// the (Popper) component's props.children, though, does receive them as expected.  It's a bit puzzling since
					// the actions menu should theoretically be considered a child node as well.
					//
					// at this point, we don't know if this is a bug, or if it's by design. A question was asked to the MUI team,
					// and it's being tracked here:
					// https://github.com/mui/material-ui/issues/33581
					//
					// A Jira story has also been created as to keep track of responses from the MUI team here:
					// https://centric8.atlassian.net/browse/C8-45358

					let callClickEventOnMouseUp = false;
					function onPopperMouseDown(event: React.MouseEvent<HTMLButtonElement & HTMLLIElement>) {
						if (!(event.target as HTMLElement).children.length) {
							callClickEventOnMouseUp = true;
						}
						event.preventDefault();
					};
					function onPopperMouseUp(event: React.MouseEvent<HTMLButtonElement & HTMLLIElement>) {
						if (callClickEventOnMouseUp) {
							action.props.onClick?.(event);
							onClose?.(null, null);
						}
					};
					return (
						<ListItem
							key={i}
							onMouseDown={onPopperMouseDown}
							onMouseUp={onPopperMouseUp}
						>
							{action}
						</ListItem>
					);
				})}
			</List>
		</StyledPaper>
	);
};

//https://github.com/mui-org/material-ui/issues/21185, have to do this way <any, boolean, boolean, boolean>
interface AutoCompleteListProps<
	T,
	Multiple extends boolean | undefined = undefined,
	DisableClearable extends boolean = undefined,
	FreeSolo extends boolean = undefined
> extends Partial<MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>> {
	/** indicates if error should happen to the input element */
	invalid?: boolean;

	/** the properties of the popper(pop up) */
	popperProps?: Partial<PopperProps>;

	/** properties of the search text */
	searchTextProps?: Omit<SearchTextFieldProps, "autoFocus" | "fullWidth" | "selectOnFocus">;

	/** Select Actions, which are displayed and stickied at the bottom of the list */
	selectActions?: ReactElement<ButtonProps>[];
}

function Autocomplete<
	T,
	Multiple extends boolean | undefined = undefined,
	DisableClearable extends boolean = undefined,
	FreeSolo extends boolean = undefined
>(props: AutoCompleteListProps<T, Multiple, DisableClearable, FreeSolo>) {
	const loc = useLocale();
	const {
		className,
		invalid,
		ListboxComponent,
		multiple,
		popperProps,
		searchTextProps,
		selectActions,
		loadingText,
		noOptionsText,
		onChange,
		onClose,
		onHighlightChange,
		onKeyDown,
		options,
		PopperComponent,
		renderOption,
		renderInput,
		value,
		...otherProps
	} = props;
	const { getOptionLabel } = props;

	// C8-39149 - useMemo avoids unnecessary re-rendering of Popper and subsequent scroll reset
	const AutocompletePopper = useMemo(() => {
		const ACPopperComponent = PopperComponent || Popper;
		return function ACPopperComponentWithSelectActions(props: PopperProps) {
			return (
				<ACPopperComponent
					placement='bottom-start'
					{...props}
					{...popperProps}
				>
					<>
						{props.children}
						{!!selectActions?.length && (
							<SelectActionsList
								onClose={onClose}
								selectActions={selectActions}
							/>
						)}
					</>
				</ACPopperComponent>
			);
		};
	}, [PopperComponent, popperProps, selectActions]);

	function defaultRenderOption(props: HTMLAttributes<HTMLLIElement>, option: T, state: AutocompleteRenderOptionState) {
		return (
			<OverflowText
				text={getOptionLabel(option)}
				tooltipProps={{ placement: "right" }}
			/>
		);
	}

	function defaultRenderInput(params: AutocompleteRenderInputParams) {
		return (
			<SearchTextField
				{...params}
				autoFocus
				fullWidth
				selectOnFocus
				{...searchTextProps}
			/>
		);
	}

	const componentClassName = `${className || ""} ${invalid ? INVALID_BORDER_CLASS : ""}`;

	const noResultsDisplayText = noOptionsText ?? loc.resource(options.length ? "NoMatches" : "NoOptions");

	const listNodeRef = useRef<HTMLElement>(null);

	// return index of focused option or -1
	function getFocusedIndex(): number {
		const elem: HTMLElement = listNodeRef.current?.querySelector(FOCUSED_OPTION_CSS);
		const index = elem?.dataset?.optionIndex;
		if (index !== undefined) {
			return Number(index);
		}
		return -1; // Return -1 to indicate that no focused item was found
	}

	// return value of focused option or -1
	function getFocusedValue(): number {
		const elem: HTMLElement = listNodeRef.current?.querySelector(FOCUSED_OPTION_CSS);
		const index = options.findIndex((o)=>(o as ComponentItem).value===elem?.dataset?.["csiValue"]);
		if (index >= 0) {
			return index;
		}
		const optionIndex = elem?.dataset?.optionIndex;
		if (optionIndex !== undefined) {
			return Number(optionIndex);
		}
		return -1; // Return -1 to indicate that no focused item was found
	}

	// focus selected option and scroll it into view
	function setFocusedIndex(index: number) {
		const listNode = listNodeRef.current;
		if (listNode) {
			// reset any currently focused or selected items
			const option = listNode.querySelector(FOCUSED_OPTION_CSS);
			if (option) {
				option.classList.remove("Mui-focused");
				option.setAttribute("aria-selected", "false");
			}

			// set the focus and selection to the specified index
			if (index >= 0 && index < options.length) {
				const newOption = listNode.querySelector(".MuiAutocomplete-option[data-option-index='" + index + "']");
				if (newOption) {
					newOption.classList.add("Mui-focused");
					newOption.setAttribute("aria-selected", "true");
					// Scroll the new focused item into view if needed
					newOption.scrollIntoView({ block: "nearest" });
				}
			}
		}
	}

	// determine if option with specified index exists (it might be filtered out)
	function indexExists(index: number): boolean {
		const listNode = listNodeRef.current;
		return !!listNode?.querySelector(".MuiAutocomplete-option[data-option-index='" + index + "']");
	}

	// disable default mui behavior for react-window up, down and enter arrow keys
	function onReactWindowKeyDown(event): KeyboardEventHandler {
		if (options?.length > largeListThreshold) {
			if (event.key !== "Dead" && listNodeRef.current) {
				const focusedIndex = getFocusedIndex();
				const total = options.length;
				let newIndex = 0;
				switch (event.key) {
					case "ArrowDown":
						stopMuiEvent(event);
						newIndex = focusedIndex + 1;
						// do not jump to the last option as it is not loaded yet
						if ((newIndex < total) && indexExists(newIndex)) {
							setFocusedIndex(newIndex);
							onHighlightChange?.(event, options[newIndex], "keyboard");
						}
						break;
					case "ArrowUp":
						stopMuiEvent(event);
						newIndex = focusedIndex - 1;
						// do not jump to the first option as it is not loaded
						if (newIndex >= 0) {
							setFocusedIndex(newIndex);
							onHighlightChange?.(event, options[newIndex], "keyboard");
						}
						break;
					case "Enter":
						stopMuiEvent(event);
						let multiValue: Array<T | AutocompleteFreeSoloValueMapping<FreeSolo>>;
						const selected = options[getFocusedValue()];
						if (!selected || (selected as ComponentItem).disabled) {
							return;
						}
						if (multiple) {
							multiValue = value as Array<T | AutocompleteFreeSoloValueMapping<FreeSolo>>;
							const idx = multiValue.indexOf(selected);
							if (idx >= 0) {
								multiValue.splice(idx, 1);
							} else {
								multiValue = multiValue.concat([selected]);
							}
						}
						onChange?.(event, (multiple? multiValue : selected) as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>, null, null);
						break;
					default:
				}
			}
		}
		onKeyDown?.(event as React.KeyboardEvent<HTMLDivElement>);
	};	

	return (
		<StyledAutoComplete<T, Multiple, DisableClearable, FreeSolo>
			ListboxComponent={ListboxComponent}
			ListboxProps={{ ref: listNodeRef }}
			PopperComponent={AutocompletePopper}
			blurOnSelect
			className={componentClassName}
			clearOnEscape
			forcePopupIcon={false}
			handleHomeEndKeys
			loadingText={loadingText ?? loc.resource("Loading")}
			multiple={multiple}
			noOptionsText={noResultsDisplayText}
			onChange={onChange}
			onClose={onClose}
			onHighlightChange={onHighlightChange}
			onKeyDown={onReactWindowKeyDown}
			options={options}
			renderInput={renderInput || defaultRenderInput}
			renderOption={renderOption || defaultRenderOption}
			value={value}
			{...otherProps}
		/>
	);
}

export default Autocomplete;